import { Link } from "react-router-dom";
import { PageHead } from "../../../components/site/PageHead";
import { SiteImage } from "../../../components/site/ProductCard";
import { SiteIcon } from "../../../components/site/SiteIcon";
import { BUSINESS, IMAGES, quotePath } from "../../../content/site";

/**
 * BR-WEB-053 — Bing Maps (Microsoft-first), with only the business address in the URL. It is a fixed
 * constant, so no visitor data can ever reach it. No embedded map (the legacy site had none).
 */
export const DIRECTIONS_URL = "https://www.bing.com/maps?where1=396%20South%20Street%2C%20Toowoomba%20QLD%204350";

/** BR-WEB-052 — the legacy office hours, worded as the legacy contact page words them. */
export const OFFICE_DAYS = "Monday to Friday";
export const OFFICE_TIMES = "9:00am to 4:00pm";

/** Each card fills its grid cell so the four line up at equal height. */
const CARD = { height: "100%" } as const;

const PHOTO_ALT = "Outdoor room with Ziptrak blinds and shutters by Greatway Patios";

/**
 * Contact — replaces legacy /contact-us (Story #25444). Static content: no network calls, no input.
 *
 * BR-WEB-050: there is deliberately NO form here. Every enquiry goes through /enquire, so the site has
 * exactly one form collecting personal information and one data path into Leads.
 */
export default function ContactPage() {
  const address = `${BUSINESS.street}, ${BUSINESS.town} ${BUSINESS.state} ${BUSINESS.postcode}`;
  return (
    <>
      <PageHead
        crumbs={[{ label: "Contact" }]}
        title="Come and see us."
        lead={`Call, email or drop in to the display centre at ${BUSINESS.street}, ${BUSINESS.town}. For a price, the quickest way is a free quote request.`}
        bg={IMAGES.bannerContact}
      />

      <div className="gw-wrap">
        <ul className="gw-cards4" aria-label="Contact details" style={{ listStyle: "none", padding: 0 }}>
          <li>
            <a className="gw-ccard" style={CARD} href={BUSINESS.tel} data-testid="contact-phone">
              <SiteIcon name="phone" size={26} />
              <small>Phone</small>
              <b>{BUSINESS.phone}</b>
              <span>Mon–Fri during office hours</span>
            </a>
          </li>
          <li>
            <a className="gw-ccard" style={CARD} href={`mailto:${BUSINESS.email}`} data-testid="contact-email">
              <SiteIcon name="mail" size={26} />
              <small>Email</small>
              <b style={{ fontSize: 15 }}>
                reception@
                <wbr />
                greatwaypatios.com.au
              </b>
              <span>Email us any time</span>
            </a>
          </li>
          <li>
            <div className="gw-ccard" style={CARD} data-testid="contact-address">
              <SiteIcon name="pin" size={26} />
              <small>Display centre</small>
              <b>
                {BUSINESS.street}
                <br />
                {BUSINESS.town}, {BUSINESS.state} {BUSINESS.postcode}
              </b>
            </div>
          </li>
          <li>
            <div className="gw-ccard" style={CARD} data-testid="contact-hours">
              <SiteIcon name="clock" size={26} />
              <small>Office hours</small>
              <b>
                {OFFICE_DAYS}
                <br />
                {OFFICE_TIMES}
              </b>
            </div>
          </li>
        </ul>
      </div>

      <section className="gw-sec" style={{ paddingTop: 64 }} aria-labelledby="contact-quote">
        <div className="gw-wrap gw-contact2">
          {/* Loading: the sandstone tile (.gw-map background) shows until the photo loads.
              Error: SiteImage hides a failed photo; the address label below stays. */}
          <div className="gw-map" data-testid="contact-photo">
            <SiteImage src={IMAGES.bannerContact} alt={PHOTO_ALT} />
            <div className="lbl">
              <SiteIcon name="pin" size={22} />
              <span>
                <b>Greatway Patios display centre</b>
                {address}
              </span>
            </div>
          </div>
          <div
            className="gw-box"
            style={{ display: "flex", flexDirection: "column", justifyContent: "center", padding: 36 }}
          >
            <p className="gw-eyebrow">Fastest way to a price</p>
            <h2 id="contact-quote" style={{ fontSize: 30 }}>
              Request a free measure &amp; quote
            </h2>
            <p style={{ marginTop: 12 }}>
              Tell us what you’re planning and where. A local rep reviews every enquiry and gets back to
              you within two business days.
            </p>
            <Link className="gw-btn gw-btn-red gw-btn-lg" to={quotePath()}>
              Get a free quote <SiteIcon name="arrow" size={18} />
            </Link>
            <a
              className="gw-btn gw-btn-line"
              href={DIRECTIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginTop: 12 }}
            >
              <SiteIcon name="pin" size={18} /> Get directions
              <span className="sr-only"> (opens Bing Maps in a new tab)</span>
            </a>
          </div>
        </div>
      </section>

      <section className="gw-sec sand tight" aria-labelledby="contact-licence">
        <div
          className="gw-wrap"
          style={{ display: "flex", gap: 28, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}
        >
          <div>
            <p className="gw-eyebrow">Licensed &amp; accountable</p>
            <h2 id="contact-licence" className="gw-h2" style={{ fontSize: 32 }}>
              QBCC licence {BUSINESS.qbcc} · Master Builders Queensland member
            </h2>
          </div>
          <Link className="gw-link" to="/about">
            About Greatway <SiteIcon name="arrow" size={16} />
          </Link>
        </div>
      </section>
    </>
  );
}
